import { useState, useEffect, useRef, useCallback } from 'react';
import { ACTIONS, EVENTS, STATUS } from 'react-joyride';
import { trackFeatureUsage } from '../utils/analytics';

/**
 * Reusable onboarding tour hook.
 * @param {string} pageId - Unique page identifier (e.g. 'trades', 'hyperliquid')
 * @param {object} [options]
 * @param {number} [options.totalSteps] - Total number of tour steps (needed for skip-to-last behavior)
 */
export function useOnboarding(pageId, { totalSteps } = {}) {
  const [tourRunning, setTourRunning] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [showWelcome, setShowWelcome] = useState(false);
  const startTimeRef = useRef(null);
  const skippedToLastRef = useRef(false);

  const completedKey = `crypnal_onboarding_${pageId}_completed`;
  const welcomeShownKey = `crypnal_onboarding_${pageId}_welcome_shown`;

  // Show welcome dialog on first visit after a delay
  useEffect(() => {
    const completed = localStorage.getItem(completedKey);
    const welcomeShown = localStorage.getItem(welcomeShownKey);

    if (!completed && !welcomeShown) {
      const timer = setTimeout(() => {
        setShowWelcome(true);
        localStorage.setItem(welcomeShownKey, 'true');
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [completedKey, welcomeShownKey]);

  const startTour = useCallback(() => {
    setShowWelcome(false);
    setStepIndex(0);
    setTourRunning(true);
    startTimeRef.current = Date.now();
    skippedToLastRef.current = false;
    trackFeatureUsage('onboarding_started', { page: pageId });
  }, [pageId]);

  const dismissWelcome = useCallback(() => {
    setShowWelcome(false);
    localStorage.setItem(completedKey, 'skipped');
    trackFeatureUsage('onboarding_skipped', { page: pageId });
  }, [completedKey, pageId]);

  const restartTour = useCallback(() => {
    setShowWelcome(false);
    setStepIndex(0);
    setTourRunning(true);
    startTimeRef.current = Date.now();
    skippedToLastRef.current = false;
    trackFeatureUsage('onboarding_restarted', { page: pageId });
  }, [pageId]);

  const getDurationSeconds = () => {
    if (!startTimeRef.current) return 0;
    return Math.round((Date.now() - startTimeRef.current) / 1000);
  };

  const handleJoyrideCallback = useCallback((data) => {
    const { action, index, status, type } = data;

    // Track step transitions
    if (type === EVENTS.STEP_AFTER || type === EVENTS.TARGET_NOT_FOUND) {
      trackFeatureUsage('onboarding_step', {
        page: pageId,
        step_index: index,
        action
      });

      if (action === ACTIONS.NEXT) {
        setStepIndex(index + 1);
      } else if (action === ACTIONS.PREV) {
        setStepIndex(index - 1);
      }
    }

    // Tour finished or skipped
    if (status === STATUS.FINISHED) {
      setTourRunning(false);
      localStorage.setItem(completedKey, 'completed');
      trackFeatureUsage('onboarding_completed', {
        page: pageId,
        duration_seconds: getDurationSeconds()
      });
    } else if (status === STATUS.SKIPPED) {
      const lastIndex = totalSteps ? totalSteps - 1 : null;
      if (lastIndex != null && !skippedToLastRef.current) {
        // Jump to the last step (help button) so the user knows how to restart.
        // Joyride internally stops on SKIPPED, so we restart at the last step on next tick.
        skippedToLastRef.current = true;
        setTourRunning(false);
        setStepIndex(lastIndex);
        setTimeout(() => setTourRunning(true), 0);
        trackFeatureUsage('onboarding_skipped_to_last', {
          page: pageId,
          skipped_from_step: index,
          duration_seconds: getDurationSeconds()
        });
        return;
      }
      setTourRunning(false);
      skippedToLastRef.current = false;
      localStorage.setItem(completedKey, 'skipped');
      trackFeatureUsage('onboarding_abandoned', {
        page: pageId,
        last_step: index,
        duration_seconds: getDurationSeconds()
      });
    }

    // Close button clicked
    if (action === ACTIONS.CLOSE) {
      setTourRunning(false);
    }
  }, [completedKey, pageId, totalSteps]);

  return {
    tourRunning,
    stepIndex,
    showWelcome,
    startTour,
    dismissWelcome,
    restartTour,
    handleJoyrideCallback
  };
}
